import { FC, useEffect, useMemo, useState } from 'react';
import ReactGA4 from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { NumberUtils, RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import {
	AdditionalService,
	carListBandService,
	dealerConfigService,
	electrificationWizardService,
	PackageDealCalculated,
	SimulationTask,
	SimulationTasksGroupedByCarListBand,
	userService
} from '@services';
import { Box, Grid, IconButton, Stack, Typography, TypographyProps } from '@mui/material';
import { DataTable, RouterLinkAnchor } from '@components/common';
import { PortalPageTitle } from '@components/portal/common';
import { WizardPackageOptionCard, WizardPackageOptionCardContent, WizardSuggestionFooter } from '@components/portal/wizard';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import { WizardServiceDetailDialog } from '../WizardServiceDetailDialog';

type TableDataType = SimulationTasksGroupedByCarListBand | SimulationTask;
const paddingX = 9;
const CellFont: FC<TypographyProps> = (props) => <Typography fontWeight={400} {...props} />;

export const WizardResultForVehicleSeller: FC = () => {
	const navigate = useNavigate();
	const [viewingService, setViewingService] = useState<AdditionalService>();
	const [tasksGroupedByCarListBand, setTasksGroupedByCarListBand] = useState<SimulationTasksGroupedByCarListBand[]>([]);
	const { data: electrificationWizard, trigger } = useService(() => electrificationWizardService.getLatest());
	const { data: carListBands } = useService(() => carListBandService.getAllFromCache());
	const { data: contactPersons = [] } = useService(() => userService.getContactPersons());
	const { data: dealerConfig } = useService(() => dealerConfigService.find(), []);
	const package1 = electrificationWizard?.packageDealsCalculated?.[0];
	const package2 = electrificationWizard?.packageDealsCalculated?.[1];
	const selectPackage = (packageDeal: PackageDealCalculated) => {
		ReactGA4.event({
			category: 'Electrification Wizard',
			action: 'Electrification Wizard - Package Option Select',
			label: `PackageId_${packageDeal.packageDealId}`
		});
		RxUtils.promisify(electrificationWizardService.selectPackage(electrificationWizard?.id || '', packageDeal.id), trigger);
	};

	useEffect(() => {
		setTasksGroupedByCarListBand(
			electrificationWizard?.simulationTasks
				? SimulationTasksGroupedByCarListBand.fromTasks(electrificationWizard.simulationTasks, carListBands)
				: []
		);
	}, [electrificationWizard, carListBands]);

	const columns = useMemo<ColumnDef<TableDataType>[]>(
		() => [
			{
				accessorKey: 'conversionListTableName',
				header: () => <Tr.Portal path="conversion-list.car-band-list" />,
				enableSorting: false,
				cell: ({ row }) => {
					return (
						<Stack direction="row" alignItems="center" spacing={1}>
							{row.getCanExpand() ? (
								<IconButton onClick={row.getToggleExpandedHandler()} title="" size="small" sx={{ color: 'common.black' }}>
									<ArrowDropDownCircleIcon
										sx={{ transform: !row.getIsExpanded() ? undefined : 'rotate(180deg)' }}
										fontSize="small"
										color="inherit"
									/>
								</IconButton>
							) : (
								<Box sx={{ width: 32 }} />
							)}
							<CellFont>{row.original.conversionListTableName}</CellFont>
						</Stack>
					);
				}
			},
			{
				accessorKey: 'conversionListTableCurrentCar',
				header: () => <Tr.Portal path="conversion-list.current-cars" />,
				enableSorting: false,
				cell: ({ row }) => (
					<CellFont>
						{row.original.conversionListTableCurrentCar?.length > 1 ? (
							<Tr.Portal path="conversion-list.various" />
						) : (
							row.original.conversionListTableCurrentCar?.[0]
						)}
					</CellFont>
				)
			},
			{
				accessorKey: 'conversionListTableEvSelection',
				header: () => <Tr.Portal path="conversion-list.final-selection" />,
				enableSorting: false,
				cell: ({ row }) => {
					return (
						<RouterLinkAnchor>
							<Typography>
								{row.original.conversionListTableEvSelection?.length <= 0 && <Tr.Portal path="conversion-list.select" />}
								{row.original.conversionListTableEvSelection?.length === 1 &&
									[
										row.original.conversionListTableEvSelection[0]?.brand,
										row.original.conversionListTableEvSelection[0]?.model
									]
										.filter(Boolean)
										.join(' ')}
								{row.original.conversionListTableEvSelection?.length > 1 && <Tr.Portal path="conversion-list.various" />}
							</Typography>
						</RouterLinkAnchor>
					);
				}
			},
			...((dealerConfig?.isVehiclePriceVisible
				? [
						{
							accessorKey: 'conversionListTableLeasing',
							header: () => <Tr.Portal path="conversion-list.leasing" />,
							enableSorting: false,
							cell: ({ row }) => (
								<Typography>
									{NumberUtils.formatAsPrice(row.original.conversionListTableLeasing, { pureDefault: true })}
								</Typography>
							)
						}
				  ]
				: []) as ColumnDef<TableDataType>[])
		],
		[dealerConfig]
	);

	return (
		<Stack direction="column" sx={{ overflow: 'hidden', height: 1.0, maxHeight: 1.0 }}>
			<Stack
				direction="column"
				spacing={5}
				sx={{ maxHeight: 1.0, flexGrow: 1, overflowY: 'hidden', paddingY: 5, paddingBottom: 0, marginBottom: 1 }}
			>
				<PortalPageTitle
					paddingX={3}
					title={<Tr.Portal path="wizard.suggestions.title" />}
					subtitle={<Tr.Portal path="wizard.suggestions.subtitle" />}
					onBackButtonClick={() => navigate('../start')}
				/>
				<Stack direction="column" spacing={5} sx={{ paddingX, overflow: 'auto' }}>
					<Stack direction="column" spacing={4}>
						<Typography variant="h4" sx={{ fontWeight: 600 }}>
							<Tr.Portal path="wizard.suggestions.selections-title" />
						</Typography>
						<DataTable columns={columns} data={tasksGroupedByCarListBand} comparator={(d1, d2) => d1.id === d2.id} />
					</Stack>
					<Stack direction="column" spacing={4}>
						<Typography variant="h4" sx={{ fontWeight: 600 }}>
							<Tr.Portal path="wizard.suggestions.options-title" />
						</Typography>
						<Grid container direction="row" sx={{ marginBottom: '4px !important' }}>
							{package1?.packageDeal ? (
								<Grid item xs={6} sx={{ height: 1.0, paddingRight: 2.75 }}>
									<WizardPackageOptionCard
										packageDeal={package1.packageDeal}
										recommended
										selected={package1.id === electrificationWizard?.selectedPackageDealCalculatedId}
										onSelect={() => selectPackage(package1)}
									>
										<WizardPackageOptionCardContent
											type="recommended"
											packageDeal={package1}
											onViewingServiceChanged={setViewingService}
										/>
									</WizardPackageOptionCard>
								</Grid>
							) : null}
							{package2?.packageDeal ? (
								<Grid item xs={6} sx={{ height: 1.0, paddingLeft: 2.75 }}>
									<WizardPackageOptionCard
										packageDeal={package2?.packageDeal}
										selected={package2.id === electrificationWizard?.selectedPackageDealCalculatedId}
										onSelect={() => selectPackage(package2)}
									>
										<WizardPackageOptionCardContent
											type="alternative"
											packageDeal={package2}
											refPackageDeal={package1}
											onViewingServiceChanged={setViewingService}
										/>
									</WizardPackageOptionCard>
								</Grid>
							) : null}
						</Grid>
					</Stack>
				</Stack>
			</Stack>
			<WizardSuggestionFooter
				showPrices={dealerConfig?.isPackageDealPriceVisible}
				electrificationWizard={electrificationWizard}
				contactPersons={contactPersons}
				leasingPerMonth={
					electrificationWizard
						? electrificationWizard.simulationTasks.reduce(
								(total, { fleetVehicle }) => total + (fleetVehicle.leasingPrice || 0),
								0
						  ) / electrificationWizard.simulationTasks.length
						: 0
				}
			/>
			<WizardServiceDetailDialog open={!!viewingService} entity={viewingService} onClose={() => setViewingService(undefined)} />
		</Stack>
	);
};
